/**
 * The clinic & operations board.
 *
 * Every room in the hospital, side by side, for one day — which is the shape
 * the job actually has. A manager running six parallel rooms is comparing them,
 * not visiting them one at a time, so the board is columns rather than a list
 * with a room filter.
 *
 * The delay is the feature this screen exists for, and it is deliberately a
 * two-step: preview, then apply. The preview is not a courtesy — it runs the
 * same planner on the server that the apply will run, so the manager confirms
 * the exact list of named patients and new times that will be written. A
 * one-click bulk mutation of a live medical schedule without a preview is not a
 * feature, it is an incident.
 *
 * The animations are CSS in styles/index.css, in the idiom the calendar and the
 * AI console already use. There is no animation library in this project and six
 * effects do not justify becoming the largest thing in the bundle.
 *
 * Dashboard 5 of the upgrade — the Clinics & Appointments manager — adds its
 * tools as tabs beside the board: the checkup requests routed here, the
 * clinics/doctors/prices/schedules, the follow-up on every patient a delay
 * moved (contacted and confirmed, or unreachable), the patients' files, and a
 * printed day.
 *
 * QA report §3.3 makes the board writable for clinics reception: "+" on a
 * column books a patient into that clinic, a waiting booking's pencil moves
 * it, its ✕ cancels it (AppointmentDialog.jsx). The patient's message for
 * each lands in the same outbox the delay fills.
 */

import { useCallback, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Field,
  Icon,
  Input,
  Modal,
  Panel,
  Spinner,
  Tabs,
  DateInput,
} from '../../components/common';
import { HeroBand, StatTile3D, StepStrip, Tile3D } from '../../components/common/Tile3D';
import { useAsync } from '../../hooks/useAsync';
import { useNotifications } from '../../context/NotificationContext';
import { usePermissions } from '../../context/PermissionContext';
import { operationsApi } from '../../api/endpoints';
import { PERMISSIONS } from '../../domain/rbac';
import { todayKey } from '../../domain/dates';
import { CheckupRequests, ClinicsManager, PrintDayButton } from './ClinicsAdmin';
import { PrintDayFilesButton } from '../hospitalShared/DayFilesPrint';
import { NoticeTracker } from '../hospitalShared/NoticeTracker';
import { PatientFinder } from '../hospitalShared/PatientFinder';
import { PatientWorkspace } from '../hospitalShared/PatientWorkspace';
import { ReceiptLookup } from '../hospitalShared/ReceiptLookup';
import { timeRange } from '../../domain/bidi';
import { LiveQueueBoard } from '../queue/LiveQueueBoard';
import { ClinicDaySheet } from './ClinicDaySheet';
import { AppointmentDialog, BOOKING_STATUS, CancelAppointmentDialog, isEditable } from './AppointmentDialog';

/** The patient files tab: find or register, then the workspace. */
function OperationsPatients({ initial = null }) {
  const [selected, setSelected] = useState(initial);
  const [creating, setCreating] = useState(false);
  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-[22rem_1fr]">
      <Panel className="h-fit">
        <PatientFinder
          selectedId={selected?.id}
          onSelect={(p) => {
            setSelected(p);
            setCreating(false);
          }}
          onCreateNew={() => {
            setSelected(null);
            setCreating(true);
          }}
        />
      </Panel>
      <Panel className="min-w-0">
        {selected || creating ? (
          <PatientWorkspace
            key={selected?.id || 'new'}
            patient={selected}
            sections={['file', 'attachments', 'route', 'medical', 'portal', 'receipts']}
            defaultSection={creating ? 'file' : 'medical'}
            onPatientChange={(p) => {
              setSelected(p);
              setCreating(false);
            }}
          />
        ) : (
          <EmptyState icon="folder-open" title="اختر ملف مريض" message="اعرض الملف واطبعه، أو عدّل بياناته، أو وجّهه لقسم." />
        )}
      </Panel>
    </div>
  );
}

/**
 * The four delays a manager actually reaches for, plus a free field below for
 * anything else. Each carries its own glyph so the row is picked from by shape
 * as much as by reading it.
 */
const PRESETS = [
  { minutes: 15, label: 'ربع ساعة', icon: 'clock' },
  { minutes: 30, label: 'نصف ساعة', icon: 'clock' },
  { minutes: 60, label: 'ساعة', icon: 'timer' },
  { minutes: 120, label: 'ساعتان', icon: 'alarm-clock' },
];

const STATUS_TONE = {
  pending: 'border-amber-500/30 bg-amber-500/10 text-amber-300',
  confirmed: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-300',
  in_progress: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300',
  completed: 'border-slate-500/30 bg-slate-500/10 text-slate-400',
  cancelled: 'border-red-500/30 bg-red-500/10 text-red-300',
};

export function OperationsBoard() {
  const { success, error: toastError } = useNotifications();
  const { can, institutionName, serviceShown } = usePermissions();

  const [date, setDate] = useState(todayKey());
  const [delayFor, setDelayFor] = useState(null);
  const [movedIds, setMovedIds] = useState([]);
  const [tab, setTab] = useState('board');
  const [noticesKey, setNoticesKey] = useState(0);
  // A patient found from a scanned receipt opens in the files tab.
  const [found, setFound] = useState(null);
  // { booking } to move one, { clinicId } to book into a column, {} from the banner.
  const [appointment, setAppointment] = useState(null);
  const [cancelling, setCancelling] = useState(null);

  const board = useAsync(() => operationsApi.board(date), [date]);
  const notices = useAsync(() => operationsApi.notices({ status: 'queued' }), []);

  const data = board.data;

  /** One column per clinic, each carrying its own day. */
  const columns = useMemo(() => {
    if (!data) return [];
    const doctorsById = new Map((data.doctors || []).map((d) => [d.id, d]));

    return (data.clinics || [])
      .map((clinic) => ({
        clinic,
        doctor: doctorsById.get(clinic.userId) || null,
        bookings: (data.bookings || [])
          .filter((b) => String(b.clinicId) === String(clinic.id))
          .sort((a, b) => String(a.time).localeCompare(String(b.time))),
      }))
      .sort((a, b) => (a.doctor?.name || '').localeCompare(b.doctor?.name || ''));
  }, [data]);

  const totals = useMemo(() => {
    const all = data?.bookings || [];
    return {
      booked: all.filter((b) => b.status !== 'cancelled').length,
      waiting: all.filter((b) => b.status === 'pending' || b.status === 'confirmed').length,
      inRoom: all.filter((b) => b.status === 'in_progress').length,
      done: all.filter((b) => b.status === 'completed').length,
    };
  }, [data]);

  const onShifted = useCallback(
    (moves) => {
      setMovedIds(moves.map((m) => String(m.bookingId)));
      board.reload();
      notices.reload();
      setNoticesKey((k) => k + 1);
      // The highlight is a hint about what just changed, not state. Cleared on
      // a timer so it does not persist into an unrelated later glance.
      setTimeout(() => setMovedIds([]), 4000);
    },
    [board, notices],
  );

  const appointmentsChanged = useCallback(() => {
    board.reload();
    notices.reload();
    setNoticesKey((k) => k + 1);
  }, [board, notices]);

  if (board.error) return <ErrorState error={board.error} onRetry={board.reload} />;

  const mayShift = can(PERMISSIONS.SCHEDULE_SHIFT);
  const mayBook = can(PERMISSIONS.APPOINTMENT_BOOK);
  const mayCancel = can(PERMISSIONS.APPOINTMENT_CANCEL);

  return (
    <div>
      <HeroBand
        icon="layout-dashboard"
        tone="emerald"
        title="استقبال العيادات"
        subtitle="كل غرف اليوم أمامك. اضغط ⏱ فوق أي عيادة لتأجيل مواعيدها وإخطار مرضاها."
      >
        {can(PERMISSIONS.PATIENT_RECORD_VIEW) && (
          <ReceiptLookup
            onFound={({ patient }) => {
              setFound(patient);
              setTab('patients');
            }}
          />
        )}
        <DateInput
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="w-auto"
        />
        {mayBook && columns.length > 0 && (
          <Button icon="calendar-plus" onClick={() => setAppointment({})}>
            حجز موعد
          </Button>
        )}
        <Button variant="ghost" icon="refresh-cw" onClick={board.reload}>
          تحديث
        </Button>
        <PrintDayButton columns={columns} date={date} institutionName={institutionName} />
        <PrintDayFilesButton date={date} institutionName={institutionName} />
      </HeroBand>

      <div className="mb-6 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <StatTile3D
          label="حجوزات اليوم"
          value={totals.booked}
          icon="calendar-check"
          tone="cyan"
          delay={0}
        />
        <StatTile3D
          label="في الانتظار"
          value={totals.waiting}
          icon="timer"
          tone="amber"
          delay={1}
        />
        <StatTile3D
          label="داخل الكشف"
          value={totals.inRoom}
          icon="stethoscope"
          tone="emerald"
          delay={2}
        />
        <StatTile3D
          label="انتهت"
          value={totals.done}
          icon="check-circle-2"
          tone="purple"
          delay={3}
        />
      </div>

      <Tabs
        tabs={[
          { id: 'board', label: 'العيادات الآن', icon: 'layout-dashboard' },
          // The day sheet: secretaries handed out, apologies, the printed جدول.
          ...(serviceShown('daySheet') ? [{ id: 'daysheet', label: 'جدول العيادات والسكرتارية', icon: 'calendar-days' }] : []),
          ...(can(PERMISSIONS.QUEUE_OVERSEE) || can(PERMISSIONS.QUEUE_ADVANCE)
            ? [{ id: 'queue', label: 'الطابور المباشر', icon: 'list-ordered' }]
            : []),
          ...(can(PERMISSIONS.CLINIC_MANAGE) || can(PERMISSIONS.QUEUE_OVERSEE)
            ? [{ id: 'checkups', label: 'طلبات الكشف', icon: 'stethoscope' }]
            : []),
          ...(can(PERMISSIONS.CLINIC_MANAGE)
            ? [{ id: 'clinics', label: 'العيادات والأطباء والأسعار', icon: 'building-2' }]
            : []),
          ...(can(PERMISSIONS.SCHEDULE_SHIFT) || can(PERMISSIONS.QUEUE_OVERSEE)
            ? [{ id: 'notices', label: 'متابعة إخطارات المرضى', icon: 'bell-ring', count: (notices.data || []).length || undefined }]
            : []),
          ...(can(PERMISSIONS.PATIENT_RECORD_VIEW) ? [{ id: 'patients', label: 'ملفات المرضى', icon: 'folder-open' }] : []),
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'queue' && <LiveQueueBoard />}
      {tab === 'daysheet' && <ClinicDaySheet initialDate={date} />}
      {tab === 'checkups' && <CheckupRequests />}
      {tab === 'clinics' && <ClinicsManager onChanged={board.reload} />}
      {tab === 'notices' && <NoticeTracker refreshKey={noticesKey} />}
      {tab === 'patients' && <OperationsPatients key={found?.id || 'none'} initial={found} />}

      {tab === 'board' && (notices.data || []).length > 0 && (
        <NoticeOutbox notices={notices.data} onSent={notices.reload} />
      )}

      {tab !== 'board' ? null : board.loading && !data ? (
        <Spinner label="جارٍ تحميل لوحة اليوم" />
      ) : columns.length === 0 ? (
        <EmptyState
          icon="building-2"
          title="لا توجد عيادات"
          message="أضف أطباء للمنشأة لتظهر عياداتهم هنا."
        />
      ) : (
        <div className="custom-scrollbar flex gap-4 overflow-x-auto pb-4">
          {columns.map((column, index) => (
            <ClinicColumn
              key={column.clinic.id}
              column={column}
              index={index}
              movedIds={movedIds}
              mayShift={mayShift}
              onDelay={() => setDelayFor(column)}
              onBook={mayBook ? () => setAppointment({ clinicId: column.clinic.id }) : null}
              onEdit={mayBook ? (booking) => setAppointment({ booking }) : null}
              onCancel={mayCancel ? (booking) => setCancelling(booking) : null}
            />
          ))}
        </div>
      )}

      <AppointmentDialog
        open={Boolean(appointment)}
        onClose={() => setAppointment(null)}
        clinics={columns}
        booking={appointment?.booking || null}
        clinicId={appointment?.clinicId || ''}
        date={date}
        onSaved={appointmentsChanged}
      />
      <CancelAppointmentDialog booking={cancelling} onClose={() => setCancelling(null)} onCancelled={appointmentsChanged} />

      <DelayModal
        column={delayFor}
        date={date}
        onClose={() => setDelayFor(null)}
        onShifted={onShifted}
        notify={{ success, toastError }}
      />
    </div>
  );
}

/** One room's day. */
function ClinicColumn({ column, index, movedIds, mayShift, onDelay, onBook, onEdit, onCancel }) {
  const { clinic, doctor, bookings } = column;

  return (
    <Panel
      padded={false}
      className="ops-column w-[290px] shrink-0"
      style={{ animationDelay: `${Math.min(index, 8) * 45}ms` }}
    >
      <div
        className="border-b border-white/5 p-4"
        style={clinic.color ? { borderTopColor: clinic.color } : undefined}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="truncate font-bold text-white">{clinic.name}</h3>
            <p className="truncate text-xs text-slate-400">{doctor?.name || '—'}</p>
          </div>
          <div className="flex shrink-0 gap-1.5">
          {onBook && (
            <button
              type="button"
              onClick={onBook}
              title="حجز موعد في هذه العيادة"
              aria-label={`حجز موعد في ${clinic.name}`}
              className="tile3d tile3d--action tone-cyan !gap-1 !rounded-xl !p-2"
            >
              <span className="tile3d__face !h-9 !w-9 !rounded-lg">
                <Icon name="plus" size={18} className="tile3d__icon" />
              </span>
            </button>
          )}
          {mayShift && (
            <button
              type="button"
              onClick={onDelay}
              title="تأجيل مواعيد هذه العيادة"
              aria-label={`تأجيل مواعيد ${clinic.name}`}
              className="tile3d tile3d--action tile3d--pulse tone-amber !gap-1 !rounded-xl !p-2"
            >
              <span className="tile3d__face !h-9 !w-9 !rounded-lg">
                <Icon name="timer" size={17} className="tile3d__icon" />
              </span>
            </button>
          )}
          </div>
        </div>

        <div className="mt-2 flex items-center gap-2 text-xs text-slate-400">
          <Icon name="clock" size={12} />
          {clinic.startTime && clinic.endTime
            ? timeRange(clinic.startTime, clinic.endTime, '—')
            : 'مواعيد محددة'}
          <span className="mr-auto">سعة {clinic.maxBookingsPerSlot || 1}</span>
        </div>
      </div>

      {bookings.length === 0 ? (
        <p className="p-6 text-center text-sm text-slate-500">لا حجوزات</p>
      ) : (
        <ul className="custom-scrollbar max-h-[58vh] divide-y divide-white/5 overflow-y-auto">
          {bookings.map((b) => (
            <li
              key={b.id}
              className={`ops-booking p-3 ${movedIds.includes(String(b.id)) ? 'ops-moved' : ''} ${
                b.status === 'cancelled' ? 'ops-booking--cancelled' : ''
              }`}
            >
              <div className="flex items-center gap-2">
                {b.status === 'in_progress' && (
                  <span className="ops-live-dot h-2 w-2 shrink-0 rounded-full bg-emerald-400" />
                )}
                <span className="font-mono text-xs font-bold text-cyan-300">{b.time}</span>
                <span className="min-w-0 flex-1 truncate text-sm text-white">
                  {b.patientName}
                </span>
                {isEditable(b) && (onEdit || onCancel) && (
                  <span className="ops-booking__actions">
                    {onEdit && (
                      <button type="button" className="ops-icon-btn" onClick={() => onEdit(b)} title="تعديل الموعد" aria-label={`تعديل موعد ${b.patientName}`}>
                        <Icon name="pencil" size={14} />
                      </button>
                    )}
                    {onCancel && (
                      <button
                        type="button"
                        className="ops-icon-btn ops-icon-btn--danger"
                        onClick={() => onCancel(b)}
                        title="إلغاء الموعد"
                        aria-label={`إلغاء موعد ${b.patientName}`}
                      >
                        <Icon name="x" size={14} />
                      </button>
                    )}
                  </span>
                )}
              </div>

              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                <Badge className={STATUS_TONE[b.status] || STATUS_TONE.pending}>{BOOKING_STATUS[b.status] || b.status}</Badge>
                {b.source === 'clinics_reception' && (
                  <span className="text-[10px] text-slate-500" title={b.bookedBy ? `حجزه ${b.bookedBy}` : undefined}>
                    حجز الاستقبال
                  </span>
                )}
                {b.status === 'cancelled' && b.cancelReason && (
                  <span className="truncate text-[11px] text-red-300/80" title={b.cancelReason}>
                    {b.cancelReason}
                  </span>
                )}
                {b.arrivalFrom && (
                  <span className="font-mono text-[11px] text-amber-300">
                    وصول {timeRange(b.arrivalFrom, b.arrivalTo)}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/**
 * Preview, then apply.
 *
 * The preview response is rendered as a before/after table with the arrival
 * window each patient will be told, plus the three counts that decide whether
 * the manager should go ahead: how many will be messaged, how many cannot be
 * reached, and how many evening slots the day has to open to absorb the delay.
 */
export function DelayModal({ column, date, onClose, onShifted, notify }) {
  const [minutes, setMinutes] = useState(60);
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const close = useCallback(() => {
    setPreview(null);
    setReason('');
    setMinutes(60);
    onClose();
  }, [onClose]);

  const runPreview = useCallback(async () => {
    setBusy(true);
    try {
      const result = await operationsApi.previewDelay({
        clinicId: column.clinic.id,
        date,
        shiftMinutes: Number(minutes),
      });
      setPreview(result);
    } catch (err) {
      notify.toastError(err.message);
    } finally {
      setBusy(false);
    }
  }, [column, date, minutes, notify]);

  const apply = useCallback(async () => {
    setBusy(true);
    try {
      const result = await operationsApi.applyDelay({
        // A client-generated idempotency key. A double-click, or a retry after
        // a flaky connection, replays the first result instead of moving the
        // day a second time.
        shiftKey: `${column.clinic.id}-${date}-${minutes}-${Date.now()}`,
        clinicId: column.clinic.id,
        date,
        shiftMinutes: Number(minutes),
        reason: reason || undefined,
      });
      notify.success(`تم تأجيل ${result.moves.length} حجز`);
      onShifted(result.moves);
      close();
    } catch (err) {
      notify.toastError(err.message);
    } finally {
      setBusy(false);
    }
  }, [column, date, minutes, reason, notify, onShifted, close]);

  if (!column) return null;

  return (
    <Modal
      open
      onClose={close}
      closeOnBackdrop={false}
      size="lg"
      icon="timer"
      title={`تأجيل مواعيد — ${column.clinic.name}`}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            إلغاء
          </Button>
          {preview ? (
            <Button onClick={apply} loading={busy} icon="check">
              تأكيد التأجيل وإخطار المرضى
            </Button>
          ) : (
            <Button onClick={runPreview} loading={busy} icon="eye">
              معاينة
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-4">
        {/* Two steps, named. Somebody doing this for the first time under
            pressure should not have to infer that "معاينة" comes first. */}
        <StepStrip steps={['اختر مدة التأخير', 'راجع المواعيد الجديدة']} current={preview ? 1 : 0} />

        <p className="rounded-xl border border-white/5 bg-slate-800/40 p-3 text-sm text-slate-300">
          كم تأخر الطبيب؟ سنؤجل كل مرضى هذه العيادة بنفس المدة، ونعطي كل مريض
          وقت حضور خاص به حتى لا يزدحم الانتظار.
        </p>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {PRESETS.map((p, i) => (
            <Tile3D
              key={p.minutes}
              icon={p.icon}
              label={p.label}
              tone={Number(minutes) === p.minutes ? 'amber' : 'slate'}
              enter
              delay={i}
              onClick={() => {
                setMinutes(p.minutes);
                setPreview(null);
              }}
            />
          ))}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="مدة التأخير (دقيقة)">
            <Input
              type="number"
              min={5}
              max={480}
              value={minutes}
              onChange={(e) => {
                setMinutes(e.target.value);
                setPreview(null);
              }}
            />
          </Field>
          <Field label="السبب" hint="يظهر في السجل، لا في رسالة المريض">
            <Input value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
        </div>

        {preview && <PreviewTable preview={preview} />}
      </div>
    </Modal>
  );
}

function PreviewTable({ preview }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Counter value={preview.moves.length} label="سينتقل" tone="cyan" />
        <Counter value={preview.notifiable} label="سيُخطر" tone="emerald" />
        <Counter value={preview.unreachable} label="بلا هاتف" tone="amber" />
        <Counter value={preview.extraHoursNeeded.length} label="مواعيد إضافية" tone="purple" />
      </div>

      {preview.unmoved.length > 0 && (
        <p className="rounded-lg border border-slate-600/40 bg-slate-800/50 p-2.5 text-xs text-slate-300">
          {preview.unmoved.length} حجز لن يتحرك (منتهٍ أو ملغى أو داخل الكشف الآن).
        </p>
      )}

      {/* Said out loud rather than discovered later: an afternoon list moved
          into an evening offer window keeps its daytime pricing. */}
      <p className="rounded-lg border border-cyan-500/20 bg-cyan-500/5 p-2.5 text-xs text-cyan-200">
        الأسعار والخصومات لا تتغير — كل حجز يحتفظ بسعره الأصلي.
      </p>

      <div className="custom-scrollbar max-h-64 overflow-y-auto rounded-xl border border-white/5">
        <table className="w-full text-right text-sm">
          <thead className="sticky top-0 bg-slate-900/95 text-xs text-slate-400">
            <tr>
              <th className="p-2 font-medium">المريض</th>
              <th className="p-2 font-medium">من</th>
              <th className="p-2 font-medium">إلى</th>
              <th className="p-2 font-medium">الحضور</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {preview.moves.map((m) => (
              <tr key={m.bookingId}>
                <td className="p-2 text-white">{m.patientName || '—'}</td>
                <td className="p-2 font-mono text-xs text-slate-400">{m.fromTime}</td>
                <td className="p-2 font-mono text-xs font-bold text-cyan-300">{m.toTime}</td>
                <td className="p-2 font-mono text-xs text-amber-300">
                  {timeRange(m.arrivalFrom, m.arrivalTo)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Counter({ value, label, tone }) {
  const tones = {
    cyan: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-300',
    emerald: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300',
    amber: 'border-amber-500/30 bg-amber-500/10 text-amber-300',
    purple: 'border-purple-500/30 bg-purple-500/10 text-purple-300',
  };
  return (
    <div className={`rounded-xl border p-2.5 text-center ${tones[tone]}`}>
      <p className="ops-value text-xl font-bold">{value}</p>
      <p className="text-[11px] opacity-80">{label}</p>
    </div>
  );
}

/**
 * The outbox.
 *
 * There is no SMS gateway in this system. Each queued row renders as a one-tap
 * wa.me link with the body already written, and the manager's own phone is the
 * transport. When a gateway is bought, a worker drains exactly these rows and
 * nothing on this screen changes.
 */
function NoticeOutbox({ notices, onSent }) {
  const { error: toastError } = useNotifications();

  const markSent = async (notice) => {
    try {
      await operationsApi.markNoticeSent(notice.id);
      onSent();
    } catch (err) {
      toastError(err.message);
    }
  };

  return (
    <Panel className="mb-6 border border-amber-500/20">
      <div className="mb-3 flex items-center gap-2">
        <Icon name="bell-ring" size={18} className="text-amber-400" />
        <h2 className="font-bold text-white">رسائل في انتظار الإرسال</h2>
        <Badge className="border-amber-500/30 bg-amber-500/10 text-amber-300">
          {notices.length}
        </Badge>
      </div>

      <ul className="custom-scrollbar max-h-56 space-y-2 overflow-y-auto">
        {notices.map((n, i) => (
          <li
            key={n.id}
            className="ops-notice flex flex-wrap items-center gap-2 rounded-xl bg-slate-800/50 p-3"
            style={{ animationDelay: `${Math.min(i, 10) * 30}ms` }}
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-bold text-white">{n.patientName || '—'}</p>
              <p className="truncate text-xs text-slate-400">{n.body}</p>
            </div>
            {n.waUrl ? (
              <a href={n.waUrl} target="_blank" rel="noreferrer noopener" className="btn-ghost">
                <Icon name="send" size={15} /> إرسال
              </a>
            ) : (
              <Badge className="border-red-500/30 bg-red-500/10 text-red-300">بلا هاتف</Badge>
            )}
            <Button variant="ghost" onClick={() => markSent(n)}>
              تم
            </Button>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

export default OperationsBoard;
